// @flow
import {
  takeEvery, put, call, take
} from 'redux-saga/effects';

import {
  getArrayResource,
  createResource,
  updateResource,
  deleteResource
} from '@redux/components/ApiRequest/actions';

import {
  DISMISS_CONFIRMATION,
  RESPONSE_CONFIRMED,
  CONFIRM_DELETE
} from '@components/ConfirmationDialog/redux/constants';
import { displayConfirmation } from '@components/ConfirmationDialog/redux/actions';
import type { Action as DismissAction } from '@components/ConfirmationDialog/types';

import { mainApiUrl } from '@lib';
import {
  EDUCATIONS_REQUEST,
  EDUCATION_CREATE,
  EDUCATION_CREATED,
  EDUCATION_UPDATE,
  EDUCATION_UPDATED,
  EDUCATION_DELETE,
  EDUCATION_DELETED
} from './constants';

import {
  updateEducations,
  errorEducations,
  createdEducation,
  errorCreateEducation,
  updatedEducation,
  errorUpdateEducation,
  deletedEducation,
  errorDeletedEducation
} from './actions';

import type {
  Action, CreateAction, UpdateAction, DeleteAction
} from '../types';

export const getResourceAction = () =>
  getArrayResource(`${mainApiUrl}/v1/candidate_degrees`, updateEducations, errorEducations);

export const createResourceAction = (action: CreateAction) =>
  createResource(
    `${mainApiUrl}/v1/candidate_degrees`,
    { candidate_degree: action.data || {} },
    createdEducation,
    errorCreateEducation
  );

export const updateResourceAction = (action: UpdateAction) =>
  updateResource(
    action.id,
    `${mainApiUrl}/v1/candidate_degrees/${action.id}`,
    { candidate_degree: action.data || {} },
    updatedEducation,
    errorUpdateEducation
  );

export const deleteResourceAction = (action: DeleteAction) =>
  deleteResource(
    action.id,
    `${mainApiUrl}/v1/candidate_degrees/${action.id}`,
    deletedEducation,
    errorDeletedEducation
  );

export function* deleteResourceFlow(action: DeleteAction): Generator<Function, void, void> {
  yield put(displayConfirmation(CONFIRM_DELETE));

  const dismissedAction: ?DismissAction = yield take(DISMISS_CONFIRMATION);

  if (dismissedAction && dismissedAction.response === RESPONSE_CONFIRMED) {
    yield put(deleteResourceAction(action));
  } else {
    yield put(errorDeletedEducation(action.id, new Error()));
  }
}

export function* educationFlow(action: Action): Generator<Function, void, void> {
  switch (action.type) {
    case EDUCATIONS_REQUEST:
      yield put(getResourceAction());
      break;

    case EDUCATION_CREATE:
      yield put(createResourceAction(action));
      break;

    case EDUCATION_UPDATE:
      yield put(updateResourceAction(action));
      break;

    case EDUCATION_DELETE:
      yield call(deleteResourceFlow, action);
      break;

    default:
      break;
  }
}

export function* educationRefetchFlow(action: Action): Generator<Function, void, void> {
  switch (action.type) {
    case EDUCATION_CREATED:
    case EDUCATION_UPDATED:
    case EDUCATION_DELETED:
      yield put(getResourceAction());
      break;

    default:
      break;
  }
}

function* educationsWatcher(): Generator<Function, void, Action> {
  yield takeEvery(
    [EDUCATIONS_REQUEST, EDUCATION_CREATE, EDUCATION_DELETE, EDUCATION_UPDATE],
    educationFlow
  );

  yield takeEvery([EDUCATION_CREATED, EDUCATION_DELETED, EDUCATION_UPDATED], educationRefetchFlow);
}

export default educationsWatcher;
